<script setup lang="ts">
  import { computed } from 'vue';
  import type { BranchMember } from '@/entities/Branch';
  import { DatePicker, IconImage, SelectInput } from '@/shared/ui';
  import { formatDateToPtBR, toNumberOrUndefined } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import { getMemberPositions } from '../../lib/getMemberPositions';
  import type { OnboardingMinistranteRow } from '../../model/useOnboardingMinistrantes';

  const props = defineProps<{
    row: OnboardingMinistranteRow;
    members: BranchMember[];
    memberOptions: { id: number; name: string }[];
    isFirst: boolean;
    isAddDisabled: boolean;
    datePickerOpen: boolean;
  }>();

  const emit = defineEmits<{
    'update:responsibleMemberId': [memberId: number | null];
    'update:responsibleUserPositionId': [positionId: number | null];
    'update:trainingDate': [date: Date | null];
    'update:datePickerOpen': [open: boolean];
    add: [];
    remove: [];
  }>();

  const positionOptions = computed(() =>
    getMemberPositions(props.members, props.row.responsibleMemberId)
  );
</script>

<template>
  <div
    data-testid="onboarding-ministrante-row"
    class="flex items-end justify-between gap-4"
  >
    <div class="grid grid-cols-[304px_348px_348px] gap-4">
      <div class="flex flex-col gap-1.5">
        <span
          :id="`onboarding-ministrante-${row.id}-member-label`"
          class="text-sm font-semibold text-gray-07"
          >Ministrante:</span
        >
        <SelectInput
          :model-value="row.responsibleMemberId ?? ''"
          :options="memberOptions"
          label="Selecione um colaborador"
          :aria-labelledby="`onboarding-ministrante-${row.id}-member-label`"
          @update:model-value="emit('update:responsibleMemberId', toNumberOrUndefined($event) ?? null)"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <span
          :id="`onboarding-ministrante-${row.id}-position-label`"
          class="text-sm font-semibold text-gray-07"
          >Cargo:</span
        >
        <SelectInput
          :model-value="row.responsibleUserPositionId ?? ''"
          :options="positionOptions"
          label="Selecione o cargo relacionado ao treinamento"
          :aria-labelledby="`onboarding-ministrante-${row.id}-position-label`"
          @update:model-value="emit('update:responsibleUserPositionId', toNumberOrUndefined($event) ?? null)"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <span
          :id="`onboarding-ministrante-${row.id}-date-label`"
          class="text-sm font-semibold text-gray-07"
          >Data do treinamento:</span
        >
        <button
          type="button"
          data-testid="onboarding-ministrante-date-trigger"
          class="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border-color)] bg-white-01 px-4 py-2.5 text-sm whitespace-nowrap focus:outline-none"
          :aria-labelledby="`onboarding-ministrante-${row.id}-date-label`"
          aria-haspopup="dialog"
          :aria-expanded="datePickerOpen"
          @click="emit('update:datePickerOpen', true)"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-5 w-5 shrink-0"
          />
          <span :class="row.trainingDate ? 'text-gray-07' : 'text-gray-03'">
            {{ row.trainingDate ? formatDateToPtBR(row.trainingDate) : 'Selecione uma data' }}
          </span>
        </button>
        <DatePicker
          :selected-date="row.trainingDate"
          :is-open="datePickerOpen"
          @update:selected-date="emit('update:trainingDate', $event)"
          @update:is-open="emit('update:datePickerOpen', $event)"
        />
      </div>
    </div>
    <div class="flex h-[46px] items-center text-[var(--theme-secondary-ink)]">
      <button
        v-if="!isFirst"
        type="button"
        aria-label="Remover ministrante"
        class="flex items-center justify-center cursor-pointer"
        @click="emit('remove')"
      >
        <IconImage
          :src="DeleteButton"
          alt="Remover ministrante"
        />
      </button>
      <button
        v-if="isFirst"
        type="button"
        aria-label="Adicionar ministrante"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
        class="flex items-center justify-center"
        :class="isAddDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
        @click="emit('add')"
      >
        <IconImage
          :src="AddButton"
          alt="Adicionar ministrante"
        />
      </button>
    </div>
  </div>
</template>
